/**
 * Core domain types — branded id types, enums, taxonomies.
 *
 * Mirrors every enum from `plans/03-architecture/data-model.md`.
 * Pure types: no runtime side-effects, no DB-dialect imports.
 */
/**
 * Convert a raw cuid2 string into a branded id (no runtime cost).
 * Use at the boundary where you have validated the id format.
 */
export const asId = (value) => value;
// ────────────────────────────────────────────────────────────────────────────
// Enums (each mirrors a `text({ enum: [...] })` from data-model.md)
// ────────────────────────────────────────────────────────────────────────────
// User
export const UI_LANGS = ['fr', 'en'];
export const THEME_MODES = ['dark', 'light', 'system'];
export const ONBOARDING_GOALS = ['inbox', 'campaigns', 'both'];
export const DEFAULT_INBOX_MODES = ['auto', 'review'];
// LinkedIn account
export const LINKEDIN_PLANS = ['free', 'premium', 'sales-navigator', 'recruiter'];
export const ACCOUNT_STATUSES = ['active', 'paused', 'error', 'limited'];
export const AI_MODES = ['auto', 'review', 'manual'];
export const OFF_TARGET_MODES = ['block', 'suggest', 'manual'];
// Voice
export const VOICE_TONES = ['formal', 'semi', 'casual'];
export const VOICE_LENGTHS = ['short', 'medium', 'mirror'];
export const VOICE_HUMORS = ['never', 'occasional', 'natural'];
export const VOICE_EMOJIS = ['never', 'light', 'yes'];
export const VOICE_LANGUAGES = ['fr', 'en', 'mirror'];
export const VOICE_FORMALITIES = ['vous', 'tu', 'mirror'];
export const NOTIFY_HOT_ACTIONS = ['auto', 'takeover', 'validate'];
export const NOTIFY_CHANNELS = ['whatsapp', 'telegram', 'slack', 'email'];
// Business
export const BUSINESS_NATURES = ['service', 'product', 'both', ''];
export const PRICING_POLICIES = ['on_demand', 'open', 'never'];
export const CONTACT_PRIORITIES = [
    'calendar_first',
    'phone_first',
    'calendar_only',
    'phone_only',
];
export const SOCIAL_KINDS = [
    'linkedin',
    'twitter',
    'instagram',
    'tiktok',
    'youtube',
    'facebook',
    'other',
];
// Campaign
export const CAMPAIGN_STATUSES = [
    'draft',
    'active',
    'paused',
    'paused_auto',
    'finished',
    'error',
];
export const CAMPAIGN_SOURCE_TYPES = ['salesnav', 'linkedin', 'csv'];
// Wave D8 — `visit` (US-224 profile-visit, no-message) + `wait` (pure
// temporal pivot) join the legacy invite/fu trio. Both consume the same
// `campaign_steps` row shape.
export const CAMPAIGN_STEP_KINDS = ['invite', 'fu1', 'fu2', 'fu3', 'visit', 'wait'];
// Wave D8 — `paused` is the cookie-cascade auto-pause state for in-flight
// runs (US-134). Distinct from `errored` (terminal) and `stopped` (campaign
// closed-out); a `paused` row resumes when the operator re-pairs the cookie.
export const CAMPAIGN_RUN_STATES = [
    'queued',
    'invited',
    'accepted',
    'fu1_sent',
    'fu2_sent',
    'fu3_sent',
    'replied',
    'errored',
    'stopped',
    'paused',
];
export const CAMPAIGN_NEXT_STEP_KINDS = [
    'invite',
    'fu1',
    'fu2',
    'fu3',
    'visit',
    'wait',
    'done',
];
export const CSV_IMPORT_STATUSES = ['pending', 'parsed', 'errored'];
// Lead
export const SOURCE_PILLS = [
    'Waalaxy',
    'LinkedIn',
    'Internal',
    'CSV',
    'Manual',
    'Unknown',
];
export const VALIDATED_FROM_VALUES = ['inbox', 'slack', 'discord', 'extension'];
export const TEMPERATURES = ['cold', 'warm', 'hot'];
export const LEAD_STATUSES = [
    'rdv_pris',
    'rdv_passe',
    'client_pot',
    'client',
    'a_relancer',
    'perdu',
];
export const CRM_TARGETS = ['hubspot', 'pipedrive', 'salesforce', 'csv', 'webhook'];
// Conversation
export const PENDING_ACTION_TYPES = ['meeting', 'call'];
export const OUTCOME_STATUSES = [
    'followup',
    'archive',
    'client',
    'won',
    'lost',
    'unknown',
];
// Message
export const MESSAGE_ROLES = ['in', 'out', 'system'];
export const OUTBOUND_SOURCES = [
    'ai_auto',
    'ai_review',
    'manual_app',
    'manual_linkedin',
    'campaign',
];
// AI draft
export const AI_DRAFT_STATUSES = [
    'pending',
    'approved',
    'sent',
    'cancelled',
    'superseded',
];
// Relance
export const RELANCE_BUILT_IN_KINDS = ['catchup', 'project', 'lastShot'];
export const RELANCE_EMITTED_STATES = [
    'queued',
    'sent',
    'cancelled_manual_reply',
    'cancelled_user',
    'errored',
];
// CRM
export const CRM_PROVIDERS = ['hubspot', 'pipedrive', 'salesforce', 'webhook'];
export const CRM_CONNECTION_STATUSES = ['active', 'errored', 'revoked'];
// Subscription
export const PLAN_LIST = ['discover', 'pro', 'scale', 'agency'];
export const SUBSCRIPTION_STATUSES = [
    'trialing',
    'active',
    'past_due',
    'canceled',
    'incomplete',
    'unpaid',
    // Wave D3 — terminal state set by the daily trial-expiry sweep when
    // `status='trialing' AND trial_ends_at < now`. Distinct from `canceled`
    // (user-initiated) so the analytics layer can tell them apart.
    'expired',
];
export const INVOICE_STATUSES = ['paid', 'open', 'void', 'uncollectible', 'draft'];
// ────────────────────────────────────────────────────────────────────────────
// Re-exported taxonomies (the prompt names these explicitly)
// ────────────────────────────────────────────────────────────────────────────
/** Canonical source pill order — left→right in UI chips. */
export const SOURCE_TAXONOMY = SOURCE_PILLS;
/** Canonical lead-status order. */
export const LEAD_STATUSES_LIST = LEAD_STATUSES;
/** Canonical lead-temperature order (cold → warm → hot). */
export const LEAD_TEMPS = TEMPERATURES;
export const DEFAULT_VOICE_PREFS = {
    tone: 'semi',
    length: 'medium',
    humor: 'occasional',
    emoji: 'light',
    language: 'mirror',
    formality: 'mirror',
    adaptive: true,
    banned: [],
    signature: [],
};
//# sourceMappingURL=types.js.map